/**
 * Public league site portals. IDs are routes: /leagues/:leagueId/home
 *
 * Compiled FALLBACK_* is used until Firestore publicLeagues/{id} loads, and
 * if a field is missing on the doc. Live list is pushed by useLeaguePortalsStore.
 */

export const FALLBACK_LEAGUE_PORTALS = [
  {
    id: "metro",
    displayName: "Metro Consolidated Hockey League",
    subtitle: "Schedule, standings and stats",
    accentColor: "#1a237e",
    logo: "/leagues/logos/metro.png",
    sponsorAbbr: "MCHL",
    isVisible: true,
    order: 0,
  },
  {
    id: "southshore",
    displayName: "South Shore Hockey League",
    subtitle: "Schedule, Standings and Stats",
    accentColor: "#1b5e20",
    logo: "/leagues/logos/southshore.png",
    sponsorAbbr: "SSHL",
    isVisible: true,
    order: 1,
  },
];

/** @deprecated Use getVisibleLeaguePortals() — kept as the static fallback list. */
export const LEAGUE_PORTALS = FALLBACK_LEAGUE_PORTALS;

const fallbackById = Object.fromEntries(
  FALLBACK_LEAGUE_PORTALS.map((p) => [p.id, p])
);

/** @type {Record<string, object>} */
let liveById: Record<string, any> = {};
/** @type {object[] | null} */
let liveList: any[] | null = null;

export function mergeLeaguePortal(
  id,
  data: Record<string, any> = {},
  fallback: Record<string, any> | undefined = fallbackById[id]
) {
  const fb: Record<string, any> = fallback || {};
  const isVisible = data.isVisible !== false;
  return {
    id,
    displayName: String(data.displayName || fb.displayName || id),
    subtitle: String(data.subtitle || fb.subtitle || ""),
    accentColor: String(data.accentColor || fb.accentColor || "#1a237e"),
    logo: String(data.logoPath || data.logo || fb.logo || ""),
    sponsorAbbr: String(
      data.sponsorAbbr || fb.sponsorAbbr || String(id).toUpperCase()
    ),
    isVisible,
    order: Number.isFinite(data.order) ? data.order : (fb.order ?? 100),
    currentSeasonId: data.currentSeasonId || fb.currentSeasonId || null,
  };
}

export function setLiveLeaguePortals(list) {
  if (!Array.isArray(list) || list.length === 0) {
    liveList = null;
    liveById = {};
    return;
  }
  liveList = list.map((p) => ({ ...p }));
  liveById = Object.fromEntries(liveList.map((p) => [p.id, p]));
}

export function getAllLeaguePortals() {
  if (liveList && liveList.length > 0) {
    return [...liveList].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }
  return FALLBACK_LEAGUE_PORTALS.map((p) => ({ ...p }));
}

export function getVisibleLeaguePortals() {
  return getAllLeaguePortals().filter((p) => p.isVisible !== false);
}

export function getLeaguePortal(leagueId) {
  if (leagueId == null) return null;
  return liveById[leagueId] || fallbackById[leagueId] || null;
}

export function isValidLeaguePortalId(leagueId) {
  if (leagueId == null || leagueId === "") return false;
  if (liveById[leagueId]) return true;
  return fallbackById[leagueId] != null;
}

export const LEAGUE_PORTAL_IDS = FALLBACK_LEAGUE_PORTALS.map((p) => p.id);

export function slugifyLeagueId(raw) {
  return String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);
}
